import { Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useLocalAnalyticsState, useSortingState } from "../../graphql/hooks";
import { SortingButton } from "../buttons/SortingButton";
import { TitleDropdown } from "../dropdowns/TitleDropdown";
import { SortingMode } from "../../models/types";
import { useApolloClient } from "@apollo/client";
import { updateDevelopmentSortingState } from "../../common/Helpers";
import { formatDateRange } from "../../common/AnalyticsHelpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faStar } from "@fortawesome/free-solid-svg-icons";
import styles from "./SortableTitles.module.scss";
import cx from "classnames";

export const SortableTitles = () => {
    const client = useApolloClient();
    const { analyticsEnabled } = useLocalAnalyticsState();
    const sortingState = useSortingState();
    const savedDateRange = localStorage.getItem("user-query-date-range");
    const parsedDateRange = savedDateRange
        ? JSON.parse(savedDateRange)
        : undefined;

    const toggleChecked = () => {
        updateDevelopmentSortingState(
            client,
            sortingState.organizationSorting,
            !sortingState.favoritesSorting
        );
    };
    const formattedDateRange = parsedDateRange
        ? formatDateRange(parsedDateRange[0], parsedDateRange[1])
        : "custom date range";

    const productColumnClass = analyticsEnabled ? "col-3" : "col-5 col-xl-4";
    const statusColumnClass = analyticsEnabled ? "col-1" : "col-2 col-xl-3";
    const orgColumnClass = analyticsEnabled ? "col-2" : "col-3";

    return (
        <Row
            className={cx(
                "d-flex text-nowrap border-bottom mb-2 pb-1 ms-1 g-0",
                styles.subtleBorder
            )}
        >
            <Col
                className={`${productColumnClass} ${styles.titles} text-inverse-50`}
            >
                <OverlayTrigger
                    delay={{
                        show: 550,
                        hide: 100
                    }}
                    overlay={
                        sortingState.favoritesSorting ? (
                            <Tooltip>Release Favorites</Tooltip>
                        ) : (
                            <Tooltip>Favorites to Top</Tooltip>
                        )
                    }
                >
                    <div
                        className={styles.starCheckbox}
                        onClick={toggleChecked}
                    >
                        <FontAwesomeIcon
                            icon={faStar}
                            className={
                                sortingState.favoritesSorting
                                    ? styles.checked
                                    : styles.unchecked
                            }
                        />
                        {sortingState.favoritesSorting && (
                            <FontAwesomeIcon
                                icon={faCheck}
                                className={styles.checkMark}
                            />
                        )}
                    </div>
                </OverlayTrigger>
                <span
                    title="Products, projects and revisions"
                    className={styles.marginToCounterSuperscript}
                >
                    <TitleDropdown
                        title="Title"
                        sortingModes={[
                            SortingMode.NameAscending,
                            SortingMode.NameDescending
                        ]}
                    />
                </span>
                <SortingButton
                    sortingModes={[
                        SortingMode.NameAscending,
                        SortingMode.NameDescending
                    ]}
                />
            </Col>

            <Col
                className={cx(
                    `${styles.titles} ps-3 text-inverse-50`,
                    orgColumnClass
                )}
            >
                <Form className="d-flex align-items-center">
                    <span className="text-inverse me-1">Organization</span>
                    {sortingState.organizationSorting && (
                        <span className={cx("d-none d-lg-block", styles.AtoZ)}>
                            (A-Z)
                        </span>
                    )}
                    <OverlayTrigger
                        overlay={<Tooltip>Group by organizations</Tooltip>}
                    >
                        <Form.Check
                            type="checkbox"
                            id="organization-checkbox"
                            className="pe-2"
                            checked={sortingState.organizationSorting}
                            onChange={() =>
                                updateDevelopmentSortingState(
                                    client,
                                    !sortingState.organizationSorting,
                                    sortingState.favoritesSorting
                                )
                            }
                        ></Form.Check>
                    </OverlayTrigger>
                </Form>
            </Col>

            <Col
                className={`col-1 ${styles.titles} justify-content-center text-inverse-50`}
            >
                <span
                    title="Last updated"
                    className={`d-flex nowrap ${styles.marginToCounterSuperscript}`}
                >
                    <TitleDropdown
                        title="Date"
                        sortingModes={[
                            SortingMode.DateAscending,
                            SortingMode.DateDescending
                        ]}
                    />
                </span>
                <SortingButton
                    sortingModes={[
                        SortingMode.DateAscending,
                        SortingMode.DateDescending
                    ]}
                />
            </Col>

            <Col
                title="Status badge contains number of projects"
                className={cx(
                    `${styles.titles} justify-content-center text-inverse`,
                    statusColumnClass
                )}
            >
                Status
            </Col>

            {analyticsEnabled ? (
                <>
                    <Col className="col-1 me-1">
                        <div
                            className={`${styles.titles} text-inverse-50`}
                            title="Impressions - previous 24 hrs"
                        >
                            <TitleDropdown
                                title="Impr"
                                sortingModes={[
                                    SortingMode.ImpressionsAscending,
                                    SortingMode.ImpressionsDescending
                                ]}
                            />
                            <span className={styles.superscript}>
                                <sup>*</sup>
                            </span>
                            <SortingButton
                                sortingModes={[
                                    SortingMode.ImpressionsAscending,
                                    SortingMode.ImpressionsDescending
                                ]}
                            />
                        </div>
                    </Col>

                    <Col className="col-1">
                        <div
                            className={`${styles.titles} text-inverse-50`}
                            title="Click-through rate (CTR) - previous 24 hrs"
                        >
                            <TitleDropdown
                                title="CTR"
                                sortingModes={[
                                    SortingMode.CtrAscending,
                                    SortingMode.CtrDescending
                                ]}
                            />
                            <span className={styles.superscript}>
                                <sup>*</sup>
                            </span>
                            <SortingButton
                                sortingModes={[
                                    SortingMode.CtrAscending,
                                    SortingMode.CtrDescending
                                ]}
                            />
                        </div>
                    </Col>

                    <Col className="col-1">
                        <div
                            className={`${styles.titles} text-inverse-50`}
                            title={`Impressions - ${formattedDateRange}`}
                        >
                            <span className={styles.marginToCounterSuperscript}>
                                <TitleDropdown
                                    title="Impr"
                                    sortingModes={[
                                        SortingMode.UserImpressionsAscending,
                                        SortingMode.UserImpressionsDescending
                                    ]}
                                />
                            </span>
                            <SortingButton
                                sortingModes={[
                                    SortingMode.UserImpressionsAscending,
                                    SortingMode.UserImpressionsDescending
                                ]}
                            />
                        </div>
                    </Col>

                    <Col className="col-1">
                        <>
                            <div
                                className={`${styles.titles} text-inverse-50`}
                                title={`Click-through rate (CTR) - ${formattedDateRange}`}
                            >
                                <span
                                    className={
                                        styles.marginToCounterSuperscript
                                    }
                                >
                                    <TitleDropdown
                                        title="CTR"
                                        sortingModes={[
                                            SortingMode.UserCtrAscending,
                                            SortingMode.UserCtrDescending
                                        ]}
                                    />
                                </span>
                                <SortingButton
                                    sortingModes={[
                                        SortingMode.UserCtrAscending,
                                        SortingMode.UserCtrDescending
                                    ]}
                                />
                            </div>
                        </>
                    </Col>
                </>
            ) : null}

            <Col className="col-1"></Col>
        </Row>
    );
};
